import React, { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { useSelectedLanguage } from '../../../../../common/hooks/useSelectedLanguage'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import { useStoreActions } from '../../../../../store/store.hooks'
import {
  deleteProtocol,
  deleteProtocolVariables,
} from '../../../../../models/graphqlTypes'
import { DELETE_PROTOCOL } from '../../../../../operations/treatmentOperations'
import {
  isAppointment,
  sortAppointmentsAndSurveys,
} from '../../utils/treatmentItem.util'
import { SaveButton } from '../../../../../common/components/SaveButton'
import { useTreatmentPanelData } from '../TreatmentPanel/TreatmentPanelProvider'

type Props = {
  isOpen: boolean
  toggleIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  treatmentSchemaId?: string | null
  onCompleted?: () => void
}

const DeleteProtocolModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  toggleIsOpen,
  treatmentSchemaId,
  onCompleted,
}) => {
  const { treatment } = useTreatmentPanelData()
  const { t } = useTranslation()

  const selectedLanguage = useSelectedLanguage()

  const setToast = useStoreActions((actions) => actions.toast.setToast)

  const futureAppointments =
    treatment?.appointments?.filter(
      (appointment) =>
        appointment.treatmentSchema?.id === treatmentSchemaId &&
        dayjs().endOf('day').isBefore(appointment.proposedDate)
    ) || []
  const isAnyFutureAppointment = futureAppointments.length > 0

  const futureSurveys =
    treatment?.surveys?.filter(
      (survey) =>
        +new Date(survey.fillableFrom) > Date.now() &&
        survey.createdByTreatmentSchemaId === treatmentSchemaId
    ) || []

  const futureAppointmentItems = sortAppointmentsAndSurveys(
    futureAppointments,
    futureSurveys
  )

  const [deleteAllAppointments, setDeleteAllAppointments] = useState(false)

  const [deleteProtocolMutation, { loading: deletingProtocol }] = useMutation<
    deleteProtocol,
    deleteProtocolVariables
  >(DELETE_PROTOCOL, {
    onCompleted: () => {
      onCompleted && onCompleted()
    },
  })

  const handleClose = () => {
    setDeleteAllAppointments(false)
    toggleIsOpen(false)
  }

  const handleSubmit = async () => {
    if (isAnyFutureAppointment && treatment && treatmentSchemaId) {
      await deleteProtocolMutation({
        variables: {
          treatmentData: {
            treatmentId: treatment.id,
            treatmentSchemaId,
          },
        },
      })
    }

    setToast({
      text: t('notification:successDeleteProtocol'),
      type: 'success',
    })

    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>{t('treatment:doctor.deleteProtocol')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {futureAppointmentItems.length
            ? t('treatment:doctor.deleteProtocolDescription')
            : t('treatment:doctor.deleteEmptyProtocolDescription')}
        </DialogContentText>
        <Box mb={2} mt={2}>
          <List dense={false}>
            {futureAppointmentItems.map((futureAppointmentItem) => {
              return (
                <ListItem key={futureAppointmentItem.id}>
                  <Box flex={2}>
                    <ListItemText
                      disableTypography
                      primary={
                        <>
                          {futureAppointmentItem.__typename === 'Appointment' &&
                            futureAppointmentItem.info?.doctorTitle[
                              selectedLanguage
                            ]}
                          {futureAppointmentItem.__typename === 'Survey' && (
                            <Typography variant="italic" fontWeight={500}>
                              {futureAppointmentItem.surveySchema.title}
                            </Typography>
                          )}
                          {futureAppointmentItem.__typename === 'Referral' && (
                            <Typography variant="italic" fontWeight={500}>
                              {futureAppointmentItem.name}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </Box>
                  <Box flex={1} pl={1}>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          variant={
                            isAppointment(futureAppointmentItem)
                              ? 'inherit'
                              : 'italic'
                          }
                          fontWeight={400}
                        >
                          {t('common:dateFormatted', {
                            date: futureAppointmentItem.date,
                          })}
                        </Typography>
                      }
                    />
                  </Box>
                </ListItem>
              )
            })}
          </List>
        </Box>
      </DialogContent>
      {isAnyFutureAppointment && (
        <DialogActions>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteAllAppointments}
                onChange={() => setDeleteAllAppointments((value) => !value)}
                name="deleteAllAppointments"
                color="primary"
              />
            }
            label={t('treatment:doctor.deleteAppointmentsConfirm')}
          />
        </DialogActions>
      )}

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common:cancel')}
        </Button>

        <SaveButton
          color="error"
          disabled={isAnyFutureAppointment && !deleteAllAppointments}
          isSaving={deletingProtocol}
          onClick={handleSubmit}
          text={t('common:delete')}
          inProgressText={t('common:deleting')}
          completedText={t('common:deleted')}
        />
      </DialogActions>
    </Dialog>
  )
}

export { DeleteProtocolModal }
